import { defineStore } from 'pinia';
import { ref } from 'vue';
import { nanoid } from 'nanoid';

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  kind: ToastKind;
  title?: string;
  message: string;
  timeoutMs: number;
}

export const useToastStore = defineStore('toast', () => {
  const items = ref<ToastItem[]>([]);

  function push(toast: Omit<ToastItem, 'id'>) {
    const id = nanoid(10);
    const full: ToastItem = { id, ...toast };
    items.value = [...items.value, full];
    if (toast.timeoutMs > 0) {
      window.setTimeout(() => dismiss(id), toast.timeoutMs);
    }
    return id;
  }

  function dismiss(id: string) {
    items.value = items.value.filter((t) => t.id !== id);
  }

  function success(message: string, title?: string, timeoutMs = 4000) {
    return push({ kind: 'success', message, title, timeoutMs });
  }
  function error(message: string, title?: string, timeoutMs = 7000) {
    return push({ kind: 'error', message, title, timeoutMs });
  }
  function warning(message: string, title?: string, timeoutMs = 5000) {
    return push({ kind: 'warning', message, title, timeoutMs });
  }
  function info(message: string, title?: string, timeoutMs = 4000) {
    return push({ kind: 'info', message, title, timeoutMs });
  }

  return { items, push, dismiss, success, error, warning, info };
});
